import React, {Fragment, useMemo, useState, useEffect, ReactNode} from 'react';
import {useFlexLayout, useTable} from 'react-table';
import {toast} from 'react-toastify';
import {DateTime} from 'luxon';
import {
  CenterCell,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TBodyRow,
} from './components/table';
import {
  FailDialog,
  ResetDialog,
  ResetSuccessDialog,
} from 'app/gakki/components/Dialog';
import {ReactComponent as DownloadIcon} from 'app/gakki/assets/download.svg';
import {ReactComponent as UploadIcon} from 'app/gakki/assets/upload.svg';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/pro-light-svg-icons';
import {Button, Tooltip} from '@mayo/mayo-ui-gakki';
import {IconButton} from 'components/Button';
import Layout from '../../components/Layout';
import PageToolBar, {ToolDivider} from '../../components/PageToolBar';
import config from '../../step-config';
import usePageData from '../../../../hooks/usePageData';
import {useXlsExport} from 'hooks/useXlsExport';
import {usePost} from 'hooks/usePost';
import {StepStatus, useAppContext} from '../../context/app-context';
import {useCompanyContext} from '../../context/company-context';
import RemainLeaveForm from './components/remain-leave-form';
import {useAuthContext, useClient} from '../../context/auth-context';
import {useMutation} from 'react-query';
import BillingDateInfo from './components/billing-date-info';
import cx from 'classnames';
import {formatYYYYMMDD} from '@gakki/utils/time';
import {deleteEmptyKey} from '@gakki/utils/validate';
import {CustomIconButton} from '@common/components/Button';

export default function RemainLeave() {
  const api = useClient();
  const {companyId} = useAuthContext();
  const {data, setData, status, edit, init, remove, updatedTime} =
    usePageData<any>('remain-leave', []);

  const {employee} = useCompanyContext();
  const {
    changeStep,
    step,
    setIsLoading,
    isLoading,
    save,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState<any>(undefined);
  const [isImport, setIsImport] = useState(false);
  const [showFailDialog, setShowFailDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [showUploadFailDialog, setShowUploadFailDialog] = useState(false);
  const formatHHMM = (HHMM: string) => {
    const timeArr = (HHMM || '').split(':');
    return `${timeArr[0]} 時 ${timeArr[1]} 分`;
  };
  const paddingZero = (number: number) => {
    let container = ['0', '0', '0'];
    let source = String(number).split('').reverse();
    return container
      .map((n, i) => (source[i] ? source[i] : n))
      .reverse()
      .join('');
  };

  const Item = (props: {
    children?: React.ReactNode;
    className?: string;
    error?: any;
  }) => {
    const {className, children, error, ...rest} = props;
    if (error) {
      return (
        <div
          className={cx(
            'min-h-[42px] !px-0',
            className,
            `${error ? 'bg-brand-red bg-opacity-[.15]' : ''}`,
          )}
          {...rest}
        >
          <Tooltip
            colorScheme="danger"
            content={'資料未正確，請點擊編輯後，修正紅色提示字欄位內容。'}
            mainAxis={0}
            placement="bottom-start"
          >
            <span className={cx('min-h-[42px] !w-full', className)}>
              {children}
            </span>
          </Tooltip>
        </div>
      );
    }
    return (
      <span className={cx('min-h-[42px]', className)} {...rest}>
        {children}
      </span>
    );
  };

  const initEmployee = (defaultDatas: any = []) => {
    return employee.map((e: any, i: number) => {
      const defaultData = defaultDatas.find((d: any) => d.employee.id === e.id);
      if (defaultData) {
        return {
          ...defaultData,
          seqNo: paddingZero(i + 1),
          employee: e,
        };
      }
      return {
        seqNo: paddingZero(i + 1),
        employee: e,
        remainingAnnualLeaves: [
          {
            availableDuration: {
              start: null,
              end: null,
            },
            availableTime: null,
            billingDate: null,
            year: null,
          },
        ],
        remainingCompensatoryLeaves: [
          {
            availableDuration: {
              start: null,
              end: null,
            },
            availableTime: null,
            year: null,
          },
        ],
        remainingPersonalLeave: {
          availableTime: null,
          year: null,
        },
        remainingSickLeave: {
          availableTime: null,
          year: null,
        },
      };
    });
  };

  const validationRemainLeave = (remainLeave: any[]) => {
    return remainLeave.map(r => {
      const transferDateObj = (d: any) => (d ? new Date(d) : undefined);

      const validate = {
        remainingAnnualLeaves: {
          availableTime: (v: any) =>
            Boolean(v?.availableDuration?.start) ||
            Boolean(v?.availableDuration?.end) ||
            Boolean(v?.year) ||
            Boolean(v?.billingDate)
              ? Boolean(v?.availableTime)
                ? null
                : '請填寫完整資訊'
              : null,
          availableDuration: (v: any) => {
            let res = null;
            if (
              Boolean(v?.availableDuration?.start) ||
              Boolean(v?.availableDuration?.end) ||
              Boolean(v?.year) ||
              Boolean(v?.billingDate)
            ) {
              const isAllFillIn =
                Boolean(v?.availableDuration?.start) &&
                Boolean(v?.availableDuration?.end);
              res = isAllFillIn ? null : '請填寫完整資訊';
            }

            let startDateObj = transferDateObj(v?.availableDuration?.start);
            let endDateObj = transferDateObj(v?.availableDuration?.end);

            if (startDateObj && endDateObj) {
              res =
                DateTime.fromJSDate(startDateObj) <=
                DateTime.fromJSDate(endDateObj)
                  ? null
                  : '迄日不可早於起始日';
            }

            return res;
          },
          billingDate: (v: any) => {
            return Boolean(v?.availableDuration?.start) ||
              Boolean(v?.availableDuration?.end) ||
              Boolean(v?.availableTime)
              ? Boolean(v?.billingDate)
                ? null
                : '請填寫完整資訊'
              : null;
          },
          year: (v: any) =>
            Boolean(v?.availableDuration?.start) ||
            Boolean(v?.availableDuration?.end) ||
            Boolean(v?.availableTime) ||
            Boolean(v?.billingDate)
              ? Boolean(v?.year)
                ? null
                : '請填寫完整資訊'
              : null,
        },
        remainingCompensatoryLeaves: {
          availableTime: (v: any) =>
            Boolean(v?.availableDuration?.start) ||
            Boolean(v?.availableDuration?.end) ||
            Boolean(v?.year)
              ? Boolean(v?.availableTime)
                ? null
                : '請填寫完整資訊'
              : null,
          availableDuration: (v: any) => {
            let res = null;
            if (
              Boolean(v?.availableDuration?.start) ||
              Boolean(v?.availableDuration?.end) ||
              Boolean(v?.year) ||
              Boolean(v?.billingDate)
            ) {
              const isAllFillIn =
                Boolean(v?.availableDuration?.start) &&
                Boolean(v?.availableDuration?.end);
              res = isAllFillIn ? null : '請填寫完整資訊';
            }

            let startDateObj = transferDateObj(v?.availableDuration?.start);
            let endDateObj = transferDateObj(v?.availableDuration?.end);
            if (startDateObj && endDateObj) {
              res =
                DateTime.fromJSDate(startDateObj) <=
                DateTime.fromJSDate(endDateObj)
                  ? null
                  : '迄日不可早於起始日';
            }
            return res;
          },
          year: (v: any) =>
            Boolean(v?.availableDuration?.start) ||
            Boolean(v?.availableDuration?.end) ||
            Boolean(v?.availableTime)
              ? Boolean(v?.year)
                ? null
                : '請填寫完整資訊'
              : null,
        },
        remainingPersonalLeave: {
          availableTime: (v: any) => {
            if (Boolean(v?.year)) {
              return Boolean(v) ? null : '請填寫完整資訊';
            }
            return null;
          },
          year: (v: any) => {
            if (Boolean(v?.availableTime)) {
              return Boolean(v) ? null : '請填寫完整資訊';
            }
            return null;
          },
        },
        remainingSickLeave: {
          availableTime: (v: any) => {
            if (Boolean(v?.year)) {
              return Boolean(v) ? null : '請填寫完整資訊';
            }
            return null;
          },
          year: (v: any) => {
            if (Boolean(v?.availableTime)) {
              return Boolean(v) ? null : '請填寫完整資訊';
            }
            return null;
          },
        },
      };

      Object.keys(r).forEach(key => {
        switch (key) {
          case 'remainingAnnualLeaves':
            r[key].map((t: any) => {
              const _err = deleteEmptyKey({
                availableTime: validate[key]?.availableTime(t),
                availableDuration: validate[key]?.availableDuration(t),
                billingDate: validate[key]?.billingDate(t),
                year: validate[key]?.year(t),
              });
              t.error = _err;
              return t;
            });
            break;
          case 'remainingCompensatoryLeaves':
            r[key].map((t: any) => {
              const _err = deleteEmptyKey({
                availableTime: validate[key]?.availableTime(t),
                availableDuration: validate[key]?.availableDuration(t),
                year: validate[key]?.year(t),
              });
              t.error = _err;
              return t;
            });
            break;
          case 'remainingPersonalLeave':
          case 'remainingSickLeave':
            let error = {};
            error = deleteEmptyKey({
              availableTime: validate[key]?.availableTime(r[key]),
              year: validate[key]?.year(r[key]),
            });
            r[key] = {...r[key], error};
            break;
          default:
            break;
        }
      });

      return r;
    });
  };

  const checkRemainLeaveError = (source: any = {}) => {
    return source.some((item: any) => {
      return Object.keys(item).some(key => {
        switch (key) {
          case 'remainingAnnualLeaves':
          case 'remainingCompensatoryLeaves':
            return item[key].some((t: any) => {
              return t?.error ? Object.keys(t.error).length > 0 : false;
            });
          case 'remainingPersonalLeave':
          case 'remainingSickLeave':
            return item[key]?.error
              ? Object.keys(item[key].error).length > 0
              : false;
          default:
            return false;
        }
      });
    });
  };

  // 匯入驗證只在 進入頁面時 & 匯入檔案後 驗證
  const setRemainLeaveError = (source: any = data, isImport?: boolean) => {
    const remainLeaveWithErrors = validationRemainLeave(source);
    if (isImport) {
      edit(remainLeaveWithErrors);
    } else {
      init(remainLeaveWithErrors);
    }
  };

  const {mutate} = usePost(
    `attendance/company/${companyId}/remaining/leave/type/import/excel`,
    {
      onSuccess: async () => {
        await fetchData();
        setIsImport(true);
        toast.success('剩餘假別已匯入，請確認資料是否正確。', {
          hideProgressBar: true,
          position: 'top-center',
        });
      },
      onError: () => {
        setErrorMessage('匯入範本格式有誤，請重新下載範本');
        setShowUploadFailDialog(true);
        setIsImport(false);
      },
      onMutate: () => {
        setIsLoading(true);
        setIsImport(false);
      },
      onSettled: () => setIsLoading(false),
    },
  );

  useEffect(() => {
    if (isImport) {
      setRemainLeaveError(data, true);
      setIsImport(false);
    }
  }, [isImport]);

  useEffect(() => {
    setIsLoading(true);
    if (data !== null && status !== StepStatus.init) {
      setData(initEmployee(data));
      setIsLoading(false);
      return;
    }
    (async () => {
      await fetchData();
      setIsImport(true);
    })();
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    return api
      .get(`attendance/company/${companyId}/remaining/leave/type`)
      .then(d => {
        if (d.data?.length > 0) {
          setData(initEmployee(d.data), StepStatus.init);
        } else {
          setData(initEmployee(), StepStatus.init);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const saveData = () => {
    return api.put(`attendance/company/${companyId}/remaining/leave/type`, {
      data,
    });
  };

  const confirmData = () => {
    return api.put(
      `attendance/company/${companyId}/remaining/leave/type/confirm`,
    );
  };
  const {mutate: handleSave} = useMutation(saveData, {
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutate: handleConfirm} = useMutation(
    () =>
      saveData()
        .then(() => refetchEnablePY('next', step + 1))
        .then(confirmData),
    {
      onSuccess: () => {
        changeStep(step + 1);
        confirm();
        remove();
      },
      onError: () => setShowFailDialog(true),
      onMutate: () => setIsLoading(true),
      onSettled: () => setIsLoading(false),
    },
  );

  const handleNext = () => {
    const hasError = checkRemainLeaveError(data);
    if (hasError) {
      setShowFailDialog(true);
      return;
    }
    handleConfirm();
  };

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      handleSave();
    }
  }, [saveAndGoStep]);

  useEffect(() => {
    const resizeCols = () => {
      setColumns(() => getColumns());
    };
    window.addEventListener('resize', resizeCols);
    return () => {
      window.removeEventListener('resize', resizeCols);
    };
  }, []);

  const getColumns = () => {
    return [
      {
        Header: '編號',
        accessor: 'seqNo',
        width: Math.round((window.innerWidth - 62) * 0.05),
        maxWidth: 50,
        //@ts-ignore
        Cell: ({row}) => {
          return (
            <span data-test-id={'seqNo'}>{paddingZero(row.index + 1)}</span>
          );
        },
      },
      {
        Header: '員工工號與姓名',
        accessor: 'employee',
        width: Math.round((window.innerWidth - 62) * 0.12),
        maxWidth: 140,
        Cell: (props: any) => {
          const {value} = props;
          return (
            <div
              className="break-all"
              data-test-id={`${props.row.index}-jobNo`}
            >{`${value.jobNo}/${value.name}`}</div>
          );
        },
      },
      {
        Header: (
          <div className="text-left">
            <div className="border-b-[1px] border-[#d5dadd] leading-[30px]">
              剩餘特休
            </div>
            <div className="text-left flex items-center h-1/2">
              <span
                className={
                  'flex items-center justify-start w-[12%] leading-[30px]'
                }
              >
                年度
              </span>
              <span
                className={
                  'flex items-center justify-start w-[25%] leading-[30px]'
                }
              >
                可用時數
              </span>
              <span
                className={
                  'flex items-center justify-start w-[39%] leading-[30px]'
                }
              >
                可用期間
              </span>
              <span
                className={
                  'flex items-center justify-start w-[24%] leading-[30px] pl-2'
                }
              >
                計薪基準日 <BillingDateInfo />
              </span>
            </div>
          </div>
        ),
        width: Math.round((window.innerWidth - 62) * 0.31),
        accessor: 'remainingAnnualLeaves',
        Cell: (props: any) => {
          const {value} = props;
          if (!value) return null;
          return value.map((annualLeave: any, idx: number) => {
            return (
              <div
                key={`remainingAnnualLeaves_${idx}`}
                className={cx(
                  `text-left relative flex items-center`,
                  '[&:not(:last-child)]:border-b [&:not(:last-child)]:border-[#d5dadd]',
                )}
              >
                <Item
                  className={
                    'flex items-center justify-start w-[12%] pl-2.5'
                  }
                  data-test-id={`${props.row.index}-annual-availableTime`}
                  error={annualLeave?.error?.year}
                >
                  {annualLeave?.year ? annualLeave?.year : '-'}
                </Item>
                <Item
                  className={
                    'flex items-center justify-start w-[25%] px-2'
                  }
                  data-test-id={`${props.row.index}-annual-availableTime`}
                  error={annualLeave?.error?.availableTime}
                >
                  {annualLeave?.availableTime
                    ? formatHHMM(annualLeave?.availableTime)
                    : '-'}
                </Item>
                <Item
                  className={
                    'flex items-center justify-start w-[39%] pl-2'
                  }
                  data-test-id={`${props.row.index}-annual-availableDuration`}
                  error={annualLeave?.error?.availableDuration}
                >
                  {annualLeave?.availableDuration?.start
                    ? `${formatYYYYMMDD(
                        annualLeave?.availableDuration?.start,
                      )} 至 ${formatYYYYMMDD(
                        annualLeave?.availableDuration?.end,
                      )}`
                    : '-'}
                </Item>
                <Item
                  className={
                    'flex items-center justify-start w-[24%] pl-2'
                  }
                  data-test-id={`${props.row.index}-annual-billingDate`}
                  error={annualLeave?.error?.billingDate}
                >
                  {annualLeave?.billingDate ? annualLeave?.billingDate : '-'}
                </Item>
              </div>
            );
          });
        },
      },
      {
        Header: (
          <div className="text-left">
            <div className="border-b-[1px] border-[#d5dadd] leading-[30px]">
              剩餘補休
            </div>
            <div className='text-left flex items-center h-1/2'>
              <span
                className={'w-[15%] flex items-center justify-start leading-[30px]'}
              >
                年度
              </span>
              <span
                className={'w-[30%] flex items-center justify-start leading-[30px]'}
              >
                可用時數
              </span>
              <span className={'flex items-center justify-start w-[55%] leading-[30px]'}>可用期間</span>
            </div>
          </div>
        ),
        width: Math.round((window.innerWidth - 62) * 0.22),
        accessor: 'remainingCompensatoryLeaves',
        Cell: (props: any) => {
          const {value} = props;
          if (!value) return null;
          return value.map((compensatoryLeave: any, idx: number) => {
            return (
              <div
                className={cx(
                  'text-left relative flex items-center',
                  '[&:not(:last-child)]:border-b [&:not(:last-child)]:border-[#d5dadd]',
                )}
                key={`remainingCompensatoryLeaves_${idx}`}
              >
                <Item
                  className={
                    'flex items-center justify-start  w-[15%] pl-2.5'
                  }
                  data-test-id={`${props.row.index}-annual-availableTime`}
                  error={compensatoryLeave?.error?.year}
                >
                  {compensatoryLeave?.year ? compensatoryLeave?.year : '-'}
                </Item>
                <Item
                  className={
                    'flex items-center justify-start  w-[30%] pl-2'
                  }
                  data-test-id={`${props.row.index}-compensatory-availableTime`}
                  error={compensatoryLeave?.error?.availableTime}
                >
                  {compensatoryLeave?.availableTime
                    ? formatHHMM(compensatoryLeave?.availableTime)
                    : '-'}
                </Item>
                <Item
                  className={'flex items-center justify-start w-[55%] pl-2'}
                  data-test-id={`${props.row.index}-compensatory-leave-availableDuration`}
                  error={compensatoryLeave?.error?.availableDuration}
                >
                  {compensatoryLeave?.availableDuration?.start
                    ? `${formatYYYYMMDD(
                        compensatoryLeave?.availableDuration?.start,
                      )} 至 ${formatYYYYMMDD(
                        compensatoryLeave?.availableDuration?.end,
                      )}`
                    : '-'}
                </Item>
              </div>
            );
          });
        },
      },
      {
        Header: (
          <div className="text-left">
            <div className="border-b-[1px] border-[#d5dadd] leading-[30px]">
              剩餘事假
            </div>
            <div>
              <span className={'w-[100%] inline-block leading-[30px]'}>
                可用時數
              </span>
            </div>
          </div>
        ),
        width: Math.round((window.innerWidth - 62) * 0.125),
        accessor: 'remainingPersonalLeave',
        //@ts-ignore
        Cell: ({value, row: {index}}) => (
          <span data-test-id={`${index}-personal-leave`} className="flex">
            <Item
              className={'w-[100%] h-10 flex items-center pl-2.5'}
              error={value?.error?.availableTime}
            >
              {value?.availableTime ? formatHHMM(value?.availableTime) : '-'}
            </Item>
          </span>
        ),
      },
      {
        Header: (
          <div className="text-left">
            <div className="border-b-[1px] border-[#d5dadd] leading-[30px]">
              剩餘有薪病假
            </div>
            <div>
              <span className={'w-[100%] inline-block leading-[30px]'}>
                可用時數
              </span>
            </div>
          </div>
        ),
        width: Math.round((window.innerWidth - 62) * 0.125),
        accessor: 'remainingSickLeave',
        //@ts-ignore
        Cell: ({value, row: {index}}) => (
          <span data-test-id={`${index}-sick-leave`} className="flex">
            <Item
              className={'w-[100%] h-10 flex items-center pl-2.5'}
              error={value?.error?.availableTime}
            >
              {value?.availableTime ? formatHHMM(value?.availableTime) : '-'}
            </Item>
          </span>
        ),
      },
      {
        Header: '功能',
        id: 'edit',
        width: Math.round((window.innerWidth - 62) * 0.05),
        maxWidth: 50,
        disableResizing: true,
        //@ts-ignore
        Cell: ({row}) => {
          return (
            <CustomIconButton
              onClick={() => {
                setShowEditForm(true);
                setEditData(row.original);
              }}
              data-test-id={'edit-btn'}
              icon="edit"
            />
          );
        },
      },
    ];
  };

  const [columns, setColumns] = useState(() => getColumns());
  const {rows, prepareRow, getTableBodyProps, getTableProps, headerGroups} =
    useTable<any>(
      {
        columns,
        data,
        getRowId: row => row.seqNo,
      },
      useFlexLayout,
    );

  const handleFormClose = () => {
    setShowEditForm(false);
    setEditData(undefined);
  };

  const handleFormSubmit = (submitData: any) => {
    const newData = data.map((d: any) => {
      if (d.seqNo === submitData.seqNo) {
        return submitData;
      }
      return d;
    });

    setData(newData);
    setIsImport(true);
    handleFormClose();
  };

  const {setData: exportExcelData} = useXlsExport();
  const downloadExcelSample = async () => {
    const downloadUrl = await api.get(
      'attendance/remaining/leave/type/sample/excel',
    );
    if (downloadUrl) {
      const response = await fetch(downloadUrl, {
        method: 'get',
        cache: 'no-cache',
      });
      const blob = await response.blob();
      exportExcelData({blob, name: `剩餘假別EXCEL範本.xlsx`});
    }
  };

  const uploadExcelSample = async (event: React.ChangeEvent<any>) => {
    const fileName = (event?.currentTarget?.files[0].name || '').split('.');
    const fileType = fileName[fileName.length - 1];
    if (['xlsx', 'xls'].includes(fileType)) {
      mutate({file: event?.currentTarget?.files[0]});
    } else {
      setErrorMessage('檔案格式限制XLSX、XLS');
      setShowUploadFailDialog(true);
    }
    event.target.value = null;
  };

  return (
    <React.Fragment>
      <Layout.MainContent>
        <PageToolBar pageTitle={config.attendance.steps.remainLeave.name}>
          <IconButton
            onClick={() => setShowResetDialog(true)}
            className={'reset-btn text-base'}
          >
            <FontAwesomeIcon icon={['fal', 'redo']} />
            全部重設
          </IconButton>
          <ToolDivider />
          <Button
            variant="simple"
            colorSchema="primary"
            onClick={downloadExcelSample}
            aria-label="download-excel-sample"
            icon={<DownloadIcon />}
          >
            Excel範本下載
          </Button>
          <ToolDivider />
          <label
            className={cx(
              'text-base bg-transparent px-[6px] py-[1px] m-0 leading-none border-0 outline-none',
              'flex items-center justify-center gap-[3px]',
              'inherit hover:text-brand cursor-pointer',
            )}
            aria-label="upload-excel-sample"
          >
            <input
              id="upload_img"
              className="hidden"
              type="file"
              accept=".xlsx,.xls"
              onChange={uploadExcelSample}
            />
            <UploadIcon />
            批次上傳
          </label>
        </PageToolBar>
        <Table {...getTableProps()}>
          <Thead>
            {headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => {
                  return (
                    <Th
                      {...column.getHeaderProps()}
                      className={cx({
                        'border-l':
                          [
                            'remainingAnnualLeaves',
                            'remainingCompensatoryLeaves',
                            'remainingPersonalLeave',
                            'remainingSickLeave',
                            'edit',
                          ].indexOf(column.id) > -1,
                        'py-0':
                          [
                            'remainingAnnualLeaves',
                            'remainingCompensatoryLeaves',
                            'remainingPersonalLeave',
                            'remainingSickLeave',
                          ].indexOf(column.id) > -1,
                      })}
                    >
                      <CenterCell className="!text-left">
                        {column.render('Header')}
                      </CenterCell>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {isLoading && (
              <div className={'text-center p-[10px]'}>
                <FontAwesomeIcon icon={faSpinner} pulse size={'2x'} />
              </div>
            )}
            {!isLoading &&
              rows.map(row => {
                prepareRow(row);
                return (
                  <TBodyRow {...row.getRowProps()} className="text-left">
                    {row.cells.map(cell => {
                      return (
                        <Td
                          className={cx({
                            'border-l':
                              [
                                'remainingAnnualLeaves',
                                'remainingCompensatoryLeaves',
                                'remainingPersonalLeave',
                                'remainingSickLeave',
                                'edit',
                              ].indexOf(cell.column.id) > -1,
                            '!p-0':
                              [
                                'remainingAnnualLeaves',
                                'remainingCompensatoryLeaves',
                                'remainingPersonalLeave',
                                'remainingSickLeave',
                              ].indexOf(cell.column.id) > -1,
                          })}
                          {...cell.getCellProps()}
                        >
                          {cell.render('Cell')}
                        </Td>
                      );
                    })}
                  </TBodyRow>
                );
              })}
          </Tbody>
        </Table>
      </Layout.MainContent>
      <Layout.Footer
        updatedTime={updatedTime}
        showNextButton
        showPrevButton
        showSaveButton
        showUpdatedTime
        disabledSaveButton={status !== StepStatus.edit || isLoading}
        disabledPrevButton={status === StepStatus.edit || isLoading}
        disabledNextButton={isLoading}
        onSave={handleSave}
        onNext={handleNext}
        onPrev={async () => {
          await onPrev();
          remove();
        }}
      />
      <ResetDialog
        isOpen={showResetDialog}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={() => {
          setData(initEmployee(), StepStatus.edit);
          setShowResetDialog(false);
          setShowResetSuccessDialog(true);
        }}
      />
      <FailDialog
        text={errorMessage}
        isOpen={showUploadFailDialog}
        onConfirm={() => setShowUploadFailDialog(false)}
        onCancel={() => setShowUploadFailDialog(false)}
      />
      <FailDialog
        isOpen={showFailDialog}
        onConfirm={() => setShowFailDialog(false)}
        onCancel={() => setShowFailDialog(false)}
      />
      <ResetSuccessDialog
        isOpen={showResetSuccessDialog}
        onCancel={() => setShowResetSuccessDialog(false)}
      />
      {showEditForm && (
        <RemainLeaveForm
          defaultValues={editData}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </React.Fragment>
  );
}
